import { Box, Typography, useTheme } from '@mui/material'
import type { ReactNode } from 'react'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import InfoIcon from '@mui/icons-material/Info'
import { Button } from './Button'

export type TErrorStateProps = {
  message?: string
  children?: ReactNode
  severity?: 'error' | 'warning' | 'info'
  showIcon?: boolean
  onRetry?: () => void
  retryText?: string
}

const ErrorState = ({
  message = 'An error occurred while loading data',
  children,
  severity = 'error',
  showIcon = true,
  onRetry,
  retryText = 'Retry',
}: TErrorStateProps) => {
  const theme = useTheme()

  const getSeverityIcon = (severity: 'error' | 'warning' | 'info') => {
    const iconStyle = { fontSize: 30 }

    switch (severity) {
      case 'warning':
        return <WarningAmberIcon sx={{ ...iconStyle, color: theme.palette.warning.main }} />
      case 'info':
        return <InfoIcon sx={{ ...iconStyle, color: theme.palette.info.main }} />
      default:
        return <ErrorOutlineIcon sx={{ ...iconStyle, color: theme.palette.error.main }} />
    }
  }

  const getSeverityBorderColor = (severity: 'error' | 'warning' | 'info') => {
    switch (severity) {
      case 'warning':
        return theme.palette.warning.main
      case 'info':
        return theme.palette.info.main
      default:
        return theme.palette.error.main
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        p: 4,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          maxWidth: '500px',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          px: 4,
          py: 2,
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'default',
          transition: 'all 0.2s ease-in-out',
        }}
      >
        {showIcon && (
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            {getSeverityIcon(severity)}
            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
                lineHeight: 1.6,
                textAlign: 'left',
              }}
            >
              {message}
            </Typography>
          </Box>
        )}

        {!showIcon && (
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: 'text.primary',
              lineHeight: 1.6,
              textAlign: 'center',
            }}
          >
            {message}
          </Typography>
        )}

        {children && <Box sx={{ mb: 3 }}>{children}</Box>}

        {onRetry && (
          <Button
            variant="outlined"
            fullWidth
            onClick={onRetry}
            size="medium"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: getSeverityBorderColor(severity),
              color: getSeverityBorderColor(severity),
              '&:hover': {
                borderColor: getSeverityBorderColor(severity),
                bgcolor: `${getSeverityBorderColor(severity)}10`,
              },
            }}
          >
            {retryText}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default ErrorState
export { ErrorState }
